/**
 * Placer un site sur la carte à partir de son adresse.
 *
 * Service : le géocodeur de la Géoplateforme (IGN), successeur de l'API Adresse
 * de l'État — gratuit, sans clé, qui couvre la métropole, la Corse et les DROM,
 * et qui accepte les appels depuis le navigateur.
 *
 * Prudence avant tout : une adresse rurale vague (« Le Bousquet ») renvoie
 * volontiers une rue homonyme dans la commune voisine. On n'accepte donc un
 * résultat d'adresse que s'il tombe dans la bonne commune avec un score
 * suffisant ; sinon on se rabat sur le centre de la commune, et on le dit
 * (`precision: 'commune'`). Un géocodage raté ne bloque jamais l'enregistrement.
 */
import type { Position } from '@/shared/donnees/types';

export const ADRESSE_GEOCODEUR = 'https://data.geopf.fr/geocodage/search';
const SCORE_MIN = 0.5;
const DELAI_MS = 6000;

interface Reponse {
  features?: {
    geometry?: { coordinates?: [number, number] };
    properties?: { score?: number; city?: string; postcode?: string; type?: string };
  }[];
}

function sansAccents(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** « Saint-Joseph » et « St Joseph » désignent la même commune. */
export function memeCommune(a: string, b: string): boolean {
  const n = (x: string) =>
    sansAccents(x).replace(/\bste?\b/g, (m) => (m === 'st' ? 'saint' : 'sainte'));
  return n(a) === n(b);
}

type Chercher = (parametres: URLSearchParams) => Promise<Reponse | null>;

const chercherEnLigne: Chercher = async (parametres) => {
  const controle = new AbortController();
  const minuterie = setTimeout(() => {
    controle.abort();
  }, DELAI_MS);
  try {
    const reponse = await fetch(`${ADRESSE_GEOCODEUR}?${parametres.toString()}`, {
      signal: controle.signal,
    });
    if (!reponse.ok) return null;
    return (await reponse.json()) as Reponse;
  } catch {
    return null;
  } finally {
    clearTimeout(minuterie);
  }
};

function premier(reponse: Reponse | null) {
  const f = reponse?.features?.[0];
  const coordonnees = f?.geometry?.coordinates;
  if (!f || !coordonnees) return null;
  const [longitude, latitude] = coordonnees;
  return {
    latitude,
    longitude,
    score: f.properties?.score ?? 0,
    commune: f.properties?.city ?? '',
  };
}

export async function geocoder(
  lieu: { adresse: string; codePostal: string; commune: string },
  chercher: Chercher = chercherEnLigne
): Promise<Position | null> {
  const codePostal = lieu.codePostal.replace(/\s/g, '');
  const commune = lieu.commune.trim();
  if (commune === '' && codePostal === '') return null;

  if (lieu.adresse.trim() !== '') {
    const p = new URLSearchParams({ q: `${lieu.adresse} ${commune}`.trim(), limit: '1' });
    if (/^\d{5}$/.test(codePostal)) p.set('postcode', codePostal);
    const trouve = premier(await chercher(p));
    if (
      trouve &&
      trouve.score >= SCORE_MIN &&
      (commune === '' || memeCommune(trouve.commune, commune))
    ) {
      return { latitude: trouve.latitude, longitude: trouve.longitude, precision: 'adresse' };
    }
  }

  const p = new URLSearchParams({ q: commune || codePostal, type: 'municipality', limit: '1' });
  if (/^\d{5}$/.test(codePostal)) p.set('postcode', codePostal);
  const ville = premier(await chercher(p));
  if (ville && (commune === '' || memeCommune(ville.commune, commune))) {
    return { latitude: ville.latitude, longitude: ville.longitude, precision: 'commune' };
  }
  return null;
}
